import { useCallback, useRef } from 'react';
import { trackEvent } from 'dibs-tracking';
import { getAbTestEligibility } from 'dibs-cookie-jar/exports/abTestHelpers';

type FeatureRampingFeatures = 'CONDENSED_PDP';
type FeatureRampingAllocation = { MIN: number; MAX: number };
type FeatureRampingAllocations = { [key in FeatureRampingFeatures]: FeatureRampingAllocation };

const FEATURE_RAMPING_ALLOCATIONS: FeatureRampingAllocations = {
    CONDENSED_PDP: { MIN: 0, MAX: 49 },
};

const isFeatureRampingVariant = ({ MIN, MAX }: FeatureRampingAllocation): boolean => {
    const { eligibility } = getAbTestEligibility(document.cookie);
    const allocation = parseInt(eligibility);

    return allocation >= MIN && allocation <= MAX;
};

const isCondensedPdpRampingVariant = (): boolean => {
    return isFeatureRampingVariant(FEATURE_RAMPING_ALLOCATIONS.CONDENSED_PDP);
};

export function trackCondensedFeatureRamping(): void {
    const searchParams = new URLSearchParams(window.location.search);
    const content = searchParams.get('utm_content');
    if (content === 'condensed') {
        const isVariant = isCondensedPdpRampingVariant();
        trackEvent({
            category: 'feature ramping',
            label: 'cpcCondensedPdp',
            action: isVariant ? 'VARIANT' : 'CONTROL',
            additional: {
                cdnFeatureRamp1: isVariant ? 'cpcCondensedPdp - test' : 'cpcRegularPdp - control',
            },
        });
    }
}

export const useCondensedCircleContentTracking = ({
    vertical,
}: {
    vertical: string;
}): {
    handleSectionVisibilityChange: ({ isVisible }: { isVisible: boolean }) => void;
    handleCircleClick: () => void;
} => {
    const hasFiredVisibilityTracking = useRef(false);
    const handleSectionVisibilityChange = useCallback(
        ({ isVisible }) => {
            if (isVisible && !hasFiredVisibilityTracking.current) {
                trackEvent({
                    category: 'promo interaction',
                    action: 'condensed pdp circle content displayed',
                    label: vertical,
                });
                hasFiredVisibilityTracking.current = true;
            }
        },
        [vertical]
    );
    const handleCircleClick = useCallback(() => {
        trackEvent({
            category: 'promo interaction',
            action: 'condensed pdp circle content clicked',
            label: vertical,
        });
    }, [vertical]);

    return {
        handleSectionVisibilityChange,
        handleCircleClick,
    };
};
